




















































import Vue from "vue";
import { DateTime } from "luxon";
import config from "@/config";

export default Vue.extend({
  name: "FormDatePicker",
  props: {
    color: {
      type: String,
      required: false,
    },
    type: {
      type: Number,
      required: false,
    },
    labelPlaceholder: {
      type: String,
      default: "",
      required: false,
    },
    isActive: {
      type: Boolean,
      default: true,
      required: false,
    },
    weekStart: {
      type: Boolean,
      default: false,
      required: false,
    },
    fullWeekPicker: {
      type: Boolean,
      default: false,
      required: false,
    },
    monthPicker: {
      type: Boolean,
      default: false,
      required: false,
    },
    disabledSelections: {
      type: Array as () => number[],
      default: () => [],
      required: false,
    },
  },
  computed: {
    timePeriodLabel(): string {
      return `${this.$t("components.cards.label.timePeriod")}`;
    },
    computedRange: {
      get(): [] {
        return this.datePicker;
      },
      set(values: any): any {
        if (this.weekStart) {
          // luxon predefined weeks to start on monday.
          const day = values[0];
          const lastWeekDay = DateTime.fromSQL(day);

          const firstDay = lastWeekDay.startOf("week").toISODate();
          const lastDay = lastWeekDay.endOf("week").toISODate();
          this.datePicker = [firstDay, lastDay];
          this.labelDisplay = `${firstDay} - ${lastDay}`;
        } else if (this.fullWeekPicker) {
          if (this.datePicker.length === 2) {
            const oldFirst = DateTime.fromISO(this.datePicker[0]);
            const oldLast = DateTime.fromISO(this.datePicker[1]);

            // This case reset the picker to a new pick
            if (oldLast.startOf("week").toMillis() !== oldFirst.toMillis()) {
              const day = DateTime.fromSQL(values[0]);
              const firstDay = day.startOf("week").toISODate();
              const lastDay = day.endOf("week").toISODate();
              this.datePicker = [firstDay, lastDay];
              this.labelDisplay = `${firstDay} - ${lastDay}`;
            } else {
              const newDay = DateTime.fromSQL(values[0]);
              // if new picked day is before the first one, we agregate the new one as the first day with the old Last one
              if (newDay.toMillis() < oldFirst.toMillis()) {
                const firstDay = newDay.startOf("week").toISODate();
                this.datePicker = [firstDay, this.datePicker[1]];
                this.labelDisplay = `${firstDay} - ${this.datePicker[1]}`;
              }
              // if new picked day is after the last one, we agregate the old first one as the first day with the new Last one
              if (newDay.toMillis() > oldLast.toMillis()) {
                const lastDay = newDay.endOf("week").toISODate();
                this.datePicker = [this.datePicker[0], lastDay];
                this.labelDisplay = `${this.datePicker[0]} - ${lastDay}`;
              }
            }
          }
        } else {
          const d = DateTime.fromSQL(`${values}-01`);
          const monthIdx = d.month - 1;
          const year = d.year;
          if (this.$i18n.locale === "en") {
            this.labelDisplay = ` ${config.monthsEN[monthIdx]} of ${year}`;
          } else {
            this.labelDisplay = ` ${config.monthsPT[monthIdx]} de ${year}`;
          }
          this.datePicker = values;
        }
      },
    },
  },
  data() {
    return {
      datePicker: [] as any,
      labelDisplay: "",
      menuModel: false,
      selectedValue: [] as any,
    };
  },
  // Default value should always be the most recent values
  // Unless if there is a pick all option. Might reconsider that then
  mounted() {
    // Handle week-based selection (if enabled)
    if (this.weekStart || this.fullWeekPicker) {
      const lastWeekDay = DateTime.now().minus({ days: 7 });
      this.datePicker.push(lastWeekDay.startOf("week").toISODate());
      this.datePicker.push(lastWeekDay.endOf("week").toISODate());

      // Format the label according to the locale
      this.labelDisplay = this.formatWeekRange(this.datePicker);
    }

    // Handle month-based selection (if enabled)
    if (this.monthPicker) {
      const currentMonth = DateTime.now().minus({ months: 1 });
      this.datePicker = currentMonth.toISODate();

      // Format the label according to the locale (Month in PT/EN)
      this.labelDisplay = this.formatMonthLabel(currentMonth);
    }
  },
  methods: {
    /** Format the label for weekly date range based on locale */
    formatWeekRange(dateRange: string[]): string {
      const startDate = DateTime.fromISO(dateRange[0]);
      const endDate = DateTime.fromISO(dateRange[1]);

      // Define options for formatting
      const options = {
        year: "2-digit",
        month: "2-digit",
        day: "numeric",
      };

      if (this.$i18n.locale === "en") {
        // English locale formatting
        return `${startDate.toLocaleString(options)} - ${endDate.toLocaleString(
          options
        )}`;
      } else {
        // Portuguese locale formatting
        return `${startDate.toLocaleString(options)} - ${endDate.toLocaleString(
          options
        )}`;
      }
    },
    /** Format the label for month-based date selection based on locale */
    formatMonthLabel(date: DateTime): string {
      const monthIdx = date.month - 1;
      const year = date.year;
      /** Adjust for locale (e.g., "de" in Portuguese, "of" in English) */
      if (this.$i18n.locale === "en") {
        return `${config.monthsEN[monthIdx]} of ${year}`;
      } else {
        return `${config.monthsPT[monthIdx]} de ${year}`;
      }
    },
    emitValueChange() {
      if (this.isActive === false) {
        return;
      }
      if (this.weekStart && this.datePicker?.length) {
        this.selectedValue = this.datePicker[0];
      } else if (this.datePicker?.length) {
        this.selectedValue = this.datePicker;
      }

      this.$emit("dateSelected", this.selectedValue);
    },
    allowedDates(val: string): boolean {
      if (this.monthPicker) {
        return (
          DateTime.fromSQL(`${val}-01`).toMillis() < DateTime.now().toMillis()
        );
      }
      const lastWeekDay = DateTime.now().minus({ days: 7 }).endOf("week");
      let extraRules = true;

      return (
        DateTime.fromSQL(val).toMillis() < lastWeekDay.toMillis() && extraRules
      );
    },
    recomputeNotch(): void {
      const f = this.$refs.periodField;
      if (f?.updateLabelWidth) f.updateLabelWidth();
      else if (f?.onResize) f.onResize();
    },
  },
  watch: {
    "$i18n.locale"() {
      /** Reformat the label based on the new locale */
      if (this.weekStart || this.fullWeekPicker) {
        this.labelDisplay = this.formatWeekRange(this.datePicker);
      } else if (this.monthPicker) {
        this.labelDisplay = this.formatMonthLabel(
          DateTime.fromISO(this.datePicker)
        );
      }
    },
    isActive(newValue) {
      if (newValue === false) {
        this.selectedValue = [];
      }
    },
  },
});
